/**
 * Privacy mode — a per-browser switch that masks money and identities.
 *
 * Deliberately NOT server state. It answers "who is standing behind me right
 * now", which changes several times a day and belongs to this browser on this
 * machine, not to the account. Persisting it per-user would sync a screen-share
 * decision made on a laptop to a phone that is not being shared.
 *
 * localStorage is wrapped in try/catch throughout: a private window, cleared
 * site data, or a browser set to block storage all make the accessor throw, and
 * a privacy toggle that crashes the admin portal would be a worse outcome than
 * one that forgets its setting.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  maskMoney as rawMaskMoney,
  maskPerson as rawMaskPerson,
  maskEmail as rawMaskEmail,
  maskText as rawMaskText,
} from '@/lib/privacyMask';

const STORAGE_KEY = 'akiva.privacyMode';

interface PrivacyContextValue {
  /** True when values should be masked. */
  privacyMode: boolean;
  toggle: () => void;
  /** Mask a pre-formatted money string, or pass it through. */
  money: (formatted: string) => string;
  /** Mask a person's name to initials, or pass it through. */
  person: (name: string | null | undefined) => string;
  email: (email: string | null | undefined) => string;
  /** Commercially sensitive free text: agency, contract number. */
  text: (value: string | null | undefined) => string;
}

const PrivacyContext = createContext<PrivacyContextValue | null>(null);

function readStored(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'on';
  } catch {
    // Storage unavailable. Default OFF: a masked screen the operator did not
    // ask for is confusing, and the switch is one click away.
    return false;
  }
}

export function PrivacyProvider({ children }: { children: ReactNode }) {
  // Server and first browser render must agree; restore browser-only state
  // after hydration rather than rendering a different tree from localStorage.
  const [privacyMode, setPrivacyMode] = useState(false);

  // Cross-tab: turning privacy on in one tab must apply to the others, since
  // the person sharing their screen is sharing all of them.
  useEffect(() => {
    setPrivacyMode(readStored());
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setPrivacyMode(e.newValue === 'on');
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const toggle = useCallback(() => {
    setPrivacyMode((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(STORAGE_KEY, next ? 'on' : 'off');
      } catch {
        // Not fatal: the toggle still works for this session.
      }
      return next;
    });
  }, []);

  const value = useMemo<PrivacyContextValue>(() => ({
    privacyMode,
    toggle,
    money: (f) => (privacyMode ? rawMaskMoney(f) : f),
    person: (n) => (privacyMode ? rawMaskPerson(n) : (n ?? '—')),
    email: (e) => (privacyMode ? rawMaskEmail(e) : (e ?? '—')),
    text: (v) => (privacyMode ? rawMaskText(v) : (v ?? '—')),
  }), [privacyMode, toggle]);

  return <PrivacyContext.Provider value={value}>{children}</PrivacyContext.Provider>;
}

/**
 * Masking helpers. Safe to call outside the provider — it degrades to showing
 * the real value rather than throwing, because a component that renders in a
 * test harness or a portal without the provider must not crash. The provider is
 * mounted at the app root, so in the running app it is always present.
 */
// Memoized fallback for when no PrivacyProvider is mounted (tests, portals).
// Must be a stable reference — consumers put `privacy` in useEffect deps.
const NO_PRIVACY: PrivacyContextValue = {
  privacyMode: false,
  toggle: () => {},
  money: (f) => f,
  person: (n) => n ?? '—',
  email: (e) => e ?? '—',
  text: (v) => v ?? '—',
};

export function usePrivacy(): PrivacyContextValue {
  const ctx = useContext(PrivacyContext);
  if (ctx) return ctx;
  return NO_PRIVACY;
}

/**
 * Format a number as currency, masked when privacy mode is on.
 *
 * Six separate money formatters exist across the admin and client surfaces
 * (task-orders/types, AdminDashboardPage, FundingTrackerPage,
 * OptionCalendarPage, OperationsAnalyticsWidget, ClientContractPage). Masking
 * at each render site would be endless and would miss the next one. This is
 * the single place a component should reach for when it prints money.
 */
export function useMoney() {
  const { money } = usePrivacy();
  return useCallback((value: number | null | undefined): string => {
    if (value === null || value === undefined) return '—';
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(value);
    return money(formatted);
  }, [money]);
}
